import React from 'react';
import { Phone } from 'lucide-react';

const FiveDaysBestSafari = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">5 DAYS BEST OF TANZANIA LUXURY SAFARI</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          5 Days Best of Tanzania Luxury Safari - Dream African Safari Tour
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Embark on a <strong>5 Days Best of Tanzania Luxury Safari</strong> to discover the finest of Tanzania. Encounter stunning wildlife such as Lions, Leopards, Elephants, Rhinoceros, Buffalos, and Cheetahs, trekking through Tanzania National Parks. This exclusive 5-day Tanzania safari package offers unparalleled game viewing and unforgettable nights with the thrilling possibility of seeing the Big Five, home to over 8 million animals and infinite nature in luxurious tents.
              </p>
            </div>

            {/* Placeholder for Lions Image */}
            <div className="mb-8">
              <div className="bg-[#f5f5f5] h-64 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                <img 
                src="/63.jpg" alt="3 Days Tanzania Wildlife Safari" className="w-full h-48 object-cover" />
              </div>
              <p className="text-center text-xs text-gray-500 mt-2">
                Lions in the Serengeti National Park
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 5 Days Best of Tanzania Luxury Safari
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Arrive in Arusha</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Our safari guide will greet you at the airport and transport you to your accommodation in Arusha. Depending on your arrival time, you may have the opportunity to unwind and prepare for the adventure of a lifetime. Arusha, a bustling hub in northern Tanzania, is a vital stopover for most safari travelers.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Gran Melia Arusha
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Transfer to Tarangire National Park</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, your safari driver guide will pick you up from your hotel and you will set off for Tarangire National Park, a drive of about two and a half hours on well-maintained tarmac roads. Named after the Tarangire River, the park serves as the critical water source for wildlife during the dry season, earning its nickname "River of Warthogs". Tarangire National Park is renowned for the large elephant herds often seen near the river, as well as giraffes, bushbucks, and hartebeests, which attract predators like lions and leopards. Tarangire is also home to more bird breeding species than anywhere on Earth, and you will marvel at the ancient baobab trees, some over a thousand years old. Enjoy a picnic lunch within the park before continuing your journey to your accommodation in Karatu after the game drive.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Kili Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 3: Transfer to Ngorongoro Crater</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, you will head into the cooler regions of the Ngorongoro Conservation Area. Upon first sight, the Ngorongoro Crater appears as a world enclosed by steep volcanic walls less an untouched wilderness featuring expansive savannahs, acacia woodlands, and shimmering lakes and swamps. The crater spans 260 km², known for its geological formations and wildlife population. Descending into the Crater, you will encounter abundant wildlife including lions, elephants, zebras, and over 500 bird species, offering a unique natural spectacle with exceptional opportunity to spot the endangered black rhino.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Kubu Kubu Tented Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 4: Serengeti National Park</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  The Serengeti is a breathtaking spectacle, renowned for its expansive open grasslands that offer exceptional wildlife viewing opportunities. Its climate and vegetation have remained largely unchanged for millions of years, preserving its natural beauty. The Serengeti is famous for the annual Great Migration, where herds of wildebeest, accompanied by zebras and antelopes, migrate in search of fresh grazing. This migration occurs year-round, with different areas offering prime viewing at various times. To maximize your experience, it’s crucial to know the best spots for wildlife sightings during each season.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Mti Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 5: Lake Manyara National Park</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, you will head to Lake Manyara National Park, a small yet remarkably diverse park located 120 km west of Arusha. Here, you will experience both compactness and grandeur, with the narrow park's landscape dramatically framed by the towering Great Rift Escarpment to the west. Lake Manyara, the park's namesake, is a shallow, saline lake that covers much of the area. Featuring thousands of flamingos, including flamingos and over 500 other bird species.
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Transportation in a 4x4 safari vehicle with a pop-up roof
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Accommodations and meals as stated
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Entrance fees to all National Parks
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking driver guide
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    English Driver guide
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Visa fees & flights
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Tips for guides
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Laundry
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International vaccinations & tests
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Health test requirements
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Personal travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Items of personal nature
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">5 Days Best of Tanzania Luxury Safari</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li>Duration: 5 Days, 4 Nights safari</li>
                  <li>Departures / Returns: Hotel pick-up. You are returned to your hotel.</li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiveDaysBestSafari;